// frontend/src/pages/Finance/Finance.jsx
// 🎨 VERSIÓN MEJORADA - DISEÑO MINIMALISTA CON DATOS REALES

import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  ButtonGroup,
  Alert,
} from 'reactstrap';
import { invoicesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { parseDate } from '../../utils/dates';

function Finance() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [filter, setFilter] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estadísticas calculadas
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
    overdueAmount: 0,
  });

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadInvoices();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    // Filtrar facturas cuando cambia el filtro
    if (filter === 'all') {
      setInvoices(allInvoices);
    } else {
      setInvoices(allInvoices.filter(inv => inv.status === filter));
    }
  }, [filter, allInvoices]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await invoicesAPI.getAll({ page_size: 100 });
      const data = response.data.results || response.data || [];
      const invoiceList = Array.isArray(data) ? data : [];
      
      setAllInvoices(invoiceList);
      setInvoices(invoiceList);
      
      // Calcular estadísticas
      const paidInvoices = invoiceList.filter(inv => inv.status === 'paid');
      const pendingInvoices = invoiceList.filter(inv => inv.status === 'pending');
      const overdueInvoices = invoiceList.filter(inv => inv.status === 'overdue');
      
      setStats({
        total: invoiceList.length,
        paid: paidInvoices.length,
        pending: pendingInvoices.length,
        overdue: overdueInvoices.length,
        subtotal: invoiceList.reduce(
          (sum, inv) => sum + parseFloat(inv.subtotal || 0),
          0
        ),
        paidSubtotal: paidInvoices.reduce(
          (sum, inv) => sum + parseFloat(inv.subtotal || 0),
          0
       ),
        pendingSubtotal: pendingInvoices.reduce(
          (sum, inv) => sum + parseFloat(inv.subtotal || 0),
          0
       ),
        overdueSubtotal: overdueInvoices.reduce(
          (sum, inv) => sum + parseFloat(inv.subtotal || 0),
          0
       ),

        totalAmount: invoiceList.reduce((sum, inv) => sum + parseFloat(inv.total || inv.amount || 0), 0),
        paidAmount: paidInvoices.reduce((sum, inv) => sum + parseFloat(inv.total || inv.amount || 0), 0),
        pendingAmount: pendingInvoices.reduce((sum, inv) => sum + parseFloat(inv.total || inv.amount || 0), 0),
        overdueAmount: overdueInvoices.reduce((sum, inv) => sum + parseFloat(inv.total || inv.amount || 0), 0),
      });
      
    } catch (err) {
      console.error('Error al cargar facturas:', err);
      setError('No se pudieron cargar las facturas');
      setInvoices([]);
      setAllInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    if (!window.confirm('¿Marcar esta factura como pagada?')) return;

    try {
      await invoicesAPI.markPaid(invoiceId, {
        payment_date: new Date().toLocaleDateString('en-CA'),
        notes: 'Marcado como pagado desde gestión de facturas'
      });
      
      setSuccess('Factura marcada como pagada exitosamente');
      loadInvoices();
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error al actualizar factura:', err);
      setError('Error al marcar la factura como pagada');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { color: 'success', text: 'Pagada', icon: 'ni-check-bold' },
      pending: { color: 'warning', text: 'Pendiente', icon: 'ni-time-alarm' },
      overdue: { color: 'danger', text: 'Vencida', icon: 'ni-fat-remove' },
      cancelled: { color: 'secondary', text: 'Cancelada', icon: 'ni-fat-remove' },
    };
    return badges[status] || badges.pending;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return parseDate(date).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando facturas...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* ============================================ */}
        {/* HEADER */}
        {/* ============================================ */}
        <Row className="mb-4">
          <Col md="6">
            <h6 className="text-white mb-0">
              <i className="ni ni-single-copy-04 me-2"></i>
              Gestión de Facturas
            </h6>
            <p className="text-white text-sm opacity-8 mb-0">
              Control y seguimiento de facturación
            </p>
          </Col>
          <Col md="6" className="text-end">
            <Button
              color="white"
              size="sm"
              className="btn-icon shadow-sm"
              onClick={loadInvoices}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-curved-next"></i>
              </span>
              <span className="btn-inner--text ms-2">Actualizar</span>
            </Button>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* ALERTAS */}
        {/* ============================================ */}
        {success && (
          <Alert color="success" className="shadow-sm" toggle={() => setSuccess(null)}>
            <span className="alert-icon"><i className="ni ni-like-2"></i></span>
            <span className="alert-text">{success}</span>
          </Alert>
        )}

        {error && (
          <Alert color="danger" className="shadow-sm" toggle={() => setError(null)}>
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
            <button 
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={loadInvoices}
            >
              <i className="ni ni-curved-next me-2"></i>
              Reintentar
            </button>
          </Alert>
        )}

        {/* ============================================ */}
        {/* STATS CARDS */}
        {/* ============================================ */}
        <Row>
          {/* Card 1: Total Facturado */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Total Facturado
                      </p>
                      {/* Subtotal */}
                      <p className="text-sm font-weight-bold mb-0 text-dark">
                        {formatCurrency(stats.subtotal)}
                      </p>
                      {/* IVA + Total */}
                      <small className="text-xs text-muted d-block">
                        + IVA = {formatCurrency(stats.totalAmount)}
                      </small>
                      {/* Conteo */}
                      <p className="mb-0 mt-1">
                      <span className="text-sm text-secondary">
                        {stats.total} facturas
                      </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-money-coins text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Cobrado */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Cobrado
                      </p>

                      {/* Subtotal */}
                      <p className="text-sm font-weight-bold mb-0 text-dark">
                        {formatCurrency(stats.paidSubtotal)}
                      </p>

                      {/* IVA + Total */}
                      <small className="text-xs text-muted d-block">
                        + IVA = {formatCurrency(stats.paidAmount)}
                      </small>

                      <p className="mb-0 mt-1">
                        <span className="text-success text-sm font-weight-bolder">
                          {stats.totalAmount > 0
                            ? Math.round((stats.paidAmount / stats.totalAmount) * 100)
                            : 0}%
                        </span>
                      <span className="text-xs text-muted"> del total</span>
                    </p>
                  </div>
                </div>

                <div className="col-4 text-end">
                  <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                    <i className="ni ni-check-bold text-lg opacity-10 text-white"></i>
                  </div>
                </div>
              </Row>
             </CardBody>
            </Card>
          </Col>

          {/* Card 3: Por Cobrar */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Por Cobrar
                      </p>
                      {/* Subtotal */}
                      <p className="text-sm font-weight-bold mb-0 text-dark">
                        {formatCurrency(stats.pendingSubtotal)}
                      </p>

                      {/* IVA + Total */}
                      <small className="text-xs text-muted d-block">
                        + IVA = {formatCurrency(stats.pendingAmount)}
                      </small>

                      <p className="mb-0 mt-1">
                      <span className="text-warning text-sm font-weight-bolder">
                        {stats.pending} facturas
                      </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-time-alarm text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 4: Vencidas */}
          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Vencidas
                      </p>

                      {/* Subtotal */}
                      <p className="text-sm font-weight-bold mb-0 text-dark">
                        {formatCurrency(stats.overdueSubtotal)}
                      </p>

                      {/* IVA + Total */}
                      <small className="text-xs text-muted d-block">
                        + IVA = {formatCurrency(stats.overdueAmount)}
                      </small>

                      <p className="mb-0 mt-1">
                      <span className="text-danger text-sm font-weight-bolder">
                        {stats.overdue} facturas
                      </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-danger shadow-danger text-center rounded-circle">
                      <i className="ni ni-fat-remove text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TABLA DE FACTURAS */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col>
            <Card className="shadow border-0 mb-4">
              <CardHeader className="pb-0 border-0">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0">
                      <i className="ni ni-bullet-list-67 me-2"></i>
                      Facturas
                    </h6>
                    <p className="text-xs text-muted mb-0 mt-1">
                      Lista completa de facturas emitidas
                    </p>
                  </Col>
                </Row>

                {/* Filtros */}
                <Row className="mt-3">
                  <Col>
                    <ButtonGroup size="sm">
                      <Button
                        color={filter === 'all' ? 'primary' : 'primary'}
                        outline={filter !== 'all'}
                        onClick={() => setFilter('all')}
                      >
                        <i className="ni ni-bullet-list-67 me-1"></i>
                        Todas ({stats.total})
                      </Button>
                      <Button
                        color={filter === 'paid' ? 'success' : 'success'}
                        outline={filter !== 'paid'}
                        onClick={() => setFilter('paid')}
                      >
                        <i className="ni ni-check-bold me-1"></i>
                        Pagadas ({stats.paid})
                      </Button>
                      <Button
                        color={filter === 'pending' ? 'warning' : 'warning'}
                        outline={filter !== 'pending'}
                        onClick={() => setFilter('pending')}
                      >
                        <i className="ni ni-time-alarm me-1"></i>
                        Pendientes ({stats.pending})
                      </Button>
                      <Button
                        color={filter === 'overdue' ? 'danger' : 'danger'}
                        outline={filter !== 'overdue'}
                        onClick={() => setFilter('overdue')}
                      >
                        <i className="ni ni-fat-remove me-1"></i>
                        Vencidas ({stats.overdue})
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Folio / Cliente
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Orden
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Monto
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Fechas
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Estado
                        </th>
                        <th className="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <i className="ni ni-archive-2 text-muted" style={{ fontSize: '3rem' }}></i>
                            <h6 className="text-muted mb-0 mt-3">
                              {filter === 'all'
                                ? 'No hay facturas registradas'
                                : `No hay facturas ${
                                    filter === 'paid'
                                      ? 'pagadas'
                                      : filter === 'pending'
                                      ? 'pendientes'
                                      : 'vencidas'
                                  }`}
                            </h6>
                            <p className="text-xs text-secondary mb-0 mt-2">
                              Las facturas aparecerán aquí cuando se generen desde las órdenes
                            </p>
                          </td>
                        </tr>
                      ) : (
                        invoices.map((invoice) => {
                          const statusBadge = getStatusBadge(invoice.status);
                          return (
                            <tr key={invoice.id} style={{ cursor: 'pointer' }}>
                              {/* Folio / Cliente */}
                              <td>
                                <div className="d-flex px-2 py-1">
                                  <div>
                                    <div className={`icon icon-shape icon-sm bg-gradient-${statusBadge.color} shadow text-center rounded-circle me-2`}>
                                      <i className="ni ni-single-copy-04 text-white text-xs"></i>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                      {invoice.folio || `FAC-${invoice.id}`}
                                    </h6>
                                    <p className="text-xs text-secondary mb-0">
                                      {invoice.client_name || 'Cliente no especificado'}
                                    </p>
                                  </div>
                                </div>
                              </td>

                              {/* Orden */}
                              <td>
                                <p className="text-sm font-weight-bold mb-0 text-dark">
                                  {invoice.order_number || 'N/A'}
                                </p>
                              </td>

                              {/* Monto */}
                              <td>
                                  <p className="text-sm font-weight-bold mb-0 text-dark">
                                    {formatCurrency(invoice.subtotal)}
                                  </p>
                                  <small className="text-xs text-muted">
                                    + IVA = {formatCurrency(invoice.total)}
                                  </small>
                                </td>

                              {/* Fechas */}
                              <td className="align-middle text-center">
                                <p className="text-xs text-secondary mb-0">
                                  <i className="ni ni-calendar-grid-58 me-1"></i>
                                  Emisión: {formatDate(invoice.issue_date)}
                                </p>
                                <p className="text-xs font-weight-bold text-dark mb-0">
                                  Vence: {formatDate(invoice.due_date)}
                                </p>
                                {invoice.payment_date && (
                                  <p className="text-xxs text-success mb-0 mt-1">
                                    <i className="ni ni-check-bold me-1"></i>
                                    Pagada: {formatDate(invoice.payment_date)}
                                  </p>
                                )}
                              </td>

                              {/* Estado */}
                              <td className="align-middle text-center">
                                <Badge color="" className={`badge-sm bg-gradient-${statusBadge.color} shadow-sm`}>
                                  <i className={`ni ${statusBadge.icon} me-1`}></i>
                                  {statusBadge.text}
                                </Badge>
                              </td>

                              {/* Acciones */}
                              <td className="align-middle text-center">
                                {invoice.pdf_url && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="text-dark font-weight-bold text-xs me-2"
                                    onClick={() => window.open(invoice.pdf_url, '_blank')}
                                  >
                                    
                                    <i className="ni ni-single-copy-04 me-1"></i>
                                    Ver Factura 
                                  </Button>
                                )}
                                {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="btn-icon shadow-sm"
                                    onClick={() => handleMarkAsPaid(invoice.id)}
                                    title="Marcar como pagada"
                                  >
                                    <span className="btn-inner--icon">
                                      <i className=""></i>
                                      Marcar como pagada
                                    </span>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Finance;
